// Contracts — кабинет Dev-отдела (work.weern.com, capability `dev`).
// Воркер: apps/work-api/src/dev.ts. Веб: apps/work-web/src/devdept.
// Формы сняты с migrations/0032 + 0034 — не выдуманы.
//
// КВОТА и РАБОТА разделены (продуктовое решение 2026-08-03): квота живёт на ячейке
// (локаль × типаж), работа — на КАРТОЧКЕ. Карточек на ячейку может быть
// сколько угодно, и заход может не выйти в свет — это нормальный исход.
import { z } from 'zod';
import { PlanCodeSchema } from '../vocab/plan';

// ─── типаж ────────────────────────────────────────────────────────────────
// Список закрытый: добавляя литерал, добавь строку в KINDS на фронте.
// Восемь = четыре категории сбора × две модальности, ровно сетка отчёта
// «Plan vs Delivered». До 2026-08-17 их было пять: фото гифт-карт и оба
// мембершипа отдел не заводил, и в отчёте эти три колонки не с чем было
// сверять — их вели руками отдельно от системы.
export const DevKindSchema = z.enum([
  'ticket_screenshot',
  'ticket_photo',
  'coupon_screenshot',
  'coupon_photo',
  'giftcard_screenshot',
  'giftcard_photo',
  'membership_screenshot',
  'membership_photo',
]);
export type DevKind = z.infer<typeof DevKindSchema>;

// ─── этапы ────────────────────────────────────────────────────────────────
// `source` — только круг 1, `fixes` — только круг 2 (доделка после отгрузки).
// 'design' — работа дизайнера над исходником. У мембершипов это ЕДИНСТВЕННЫЙ
// производственный этап: манифеста у них нет вовсе, и как только дизайнер сдал
// исходник, карточка идёт сразу на QA (решение владельца 2026-08-17).
export const DevStageSchema = z.enum([
  'source', 'fixes', 'design', 'manifest', 'manifest_qa', 'replicate',
  'material_qa', 'print', 'shoot', 'shoot_qa', 'shipped',
]);
export type DevStage = z.infer<typeof DevStageSchema>;

// 'master' — мастер-кит от дизайнера (AD-3 dev-плагина): manifest ∧ master =
// карточка видна в плагине размножения.
// Перечисление обязано покрывать ВСЕ значения kind, которые кто-либо пишет в
// dev_card_artifacts: loadCards отдаёт строки таблицы как есть, и незнакомый kind
// валит разбор всего /api/work/dev/overview, а не одну карточку. 'design' сюда
// пишет привязка дизайна tick (devdesign.ts, витрина рядом с донором).
// UI-союз ArtifactKind в work-web — это отдельный, более узкий список слотов
// карточки: 'master' и 'design' в нём нет намеренно, toCard такие строки пропускает.
export const DevArtifactKindSchema = z.enum([
  'source', 'manifest', 'output', 'reverse', 'master', 'design',
]);
export type DevArtifactKind = z.infer<typeof DevArtifactKindSchema>;

export const DevActionSchema = z.enum(['upload', 'replace', 'download']);
export type DevAction = z.infer<typeof DevActionSchema>;

/** Чем кончился заход. '' — идёт; 'dropped' — не вышел в свет, и это не ошибка. */
export const DevOutcomeSchema = z.enum(['', 'shipped', 'dropped']);
export type DevOutcome = z.infer<typeof DevOutcomeSchema>;

// ─── материал карточки ────────────────────────────────────────────────────
export const DevArtifactSchema = z.object({
  /**
   * Позиция внутри карточки, которой принадлежит материал (мигр. 0054).
   * Пусто — материал КАРТОЧКИ ЦЕЛИКОМ: у билетов один манифест и один PDF под
   * печать, фиктивная позиция им ни к чему. У гифт-карт позиций несколько, и у
   * каждой свои исходник, манифест и паки.
   */
  item: z.string().default(''),
  kind: DevArtifactKindSchema,
  present: z.boolean(),
  file_name: z.string().default(''),
  version: z.number().int().nonnegative().default(0),
  /** Кто залил последнюю версию — справка, не отчётность. */
  by: z.string().default(''),
  at: z.string().default(''),
  /** Для обратного манифеста — сколько позиций отклонено. */
  count: z.number().int().nonnegative().default(0),
});
export type DevArtifact = z.infer<typeof DevArtifactSchema>;

// ─── ячейка = квота ───────────────────────────────────────────────────────
// Ни этапа, ни площадки: ячейка только считает цель и накопленный факт.
export const DevQuotaSchema = z.object({
  id: z.string(),                 // 'sv_SE|ticket_photo'
  locale: z.string(),
  kind: DevKindSchema,
  /** ЦЕЛЬ. Берётся из locale_limits, а не из факта производства. */
  plan: z.number().int().nonnegative(),
  /** Отгружено накопительно = всё, что не отклонено заказчиком. */
  shipped: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  note: z.string().default(''),
  updated_at: z.string().default(''),
});
export type DevQuota = z.infer<typeof DevQuotaSchema>;

// ─── карточка = заход ─────────────────────────────────────────────────────
export const DevCardSchema = z.object({
  id: z.string(),
  quota_id: z.string(),           // 'sv_SE|ticket_photo'
  locale: z.string(),
  kind: DevKindSchema,
  /** Площадка. Свободный ввод — справочника нет намеренно. */
  source: z.string().default(''),
  /** «Нужна работа дизайнера» на карточке целиком (мигр. 0053). */
  needs_design: z.boolean().default(false),
  /** Позиции внутри, помеченные «нужен дизайнер» — имена. */
  needs_design_items: z.array(z.string()).default([]),
  /**
   * Позиции, у которых исходник УЖЕ есть — в индексе или в архивах: имя → ссылка.
   * Кабинет не должен предлагать загрузить то, что уже хранится.
   */
  source_refs: z.record(z.string(), z.string()).default({}),
  stage: DevStageSchema,
  round: z.union([z.literal(1), z.literal(2)]),
  /** Сколько должен дать ЭТОТ заход (не квота ячейки). */
  target: z.number().int().nonnegative(),
  made: z.number().int().nonnegative(),
  on_qa: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  shipped: z.number().int().nonnegative(),
  merchant_plan: z.number().int().nonnegative(),
  /** Распарсено из последнего манифеста. */
  merchants: z.array(z.string()).default([]),
  /** Из них уже уехали заказчику — на доделке их не повторяют. */
  merchants_shipped: z.array(z.string()).default([]),
  manifest_version: z.number().int().nonnegative().default(0),
  outcome: DevOutcomeSchema.default(''),
  drop_reason: z.string().default(''),
  note: z.string().default(''),
  created_at: z.string().default(''),
  updated_at: z.string().default(''),
  artifacts: z.array(DevArtifactSchema).default([]),
});
export type DevCard = z.infer<typeof DevCardSchema>;

// ─── замечание QA ─────────────────────────────────────────────────────────
// Автор наружу НЕ отдаётся: отделу важно, что это QA, а не кто именно.
export const DevQaCommentSchema = z.object({
  id: z.string(),
  card_id: z.string(),
  quota_id: z.string().default(''),
  target: z.enum(['frame', 'manifest', 'pdf']).default('frame'),
  target_label: z.string().default(''),
  text: z.string(),
  created_at: z.string(),
  /** «Взято в работу» — карточка ушла на доделку. '' = ещё не брали. */
  accepted_at: z.string().default(''),
  /**
   * «Исправлено» — замечание закрыла КОНКРЕТНАЯ перезаливка (мигр. 0051).
   * Отдельно от `accepted_at`, потому что взятие в работу гасит разом все
   * замечания карточки, и после него не сказать, что именно из них сделано.
   */
  fixed_at: z.string().default(''),
  fixed_by: z.string().default(''),
  /** Версия итогового файла на момент закрытия — чем именно закрыли. */
  fix_version: z.number().int().nonnegative().default(0),
});
export type DevQaComment = z.infer<typeof DevQaCommentSchema>;

// ─── POST /api/work/dev/qa/fixed — отметить замечания исправленными ───────
// Список, а не одно: сдавая правки, человек разом отмечает всё, что закрыла
// эта заливка. Неотмеченное остаётся висеть — это и есть ответ на «какие
// ошибки ещё не сделаны».
export const DevMarkFixedReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  comment_ids: z.array(z.string().trim().min(1).max(64)).max(200).default([]),
});
export type DevMarkFixedReq = z.infer<typeof DevMarkFixedReqSchema>;

// ─── GET /api/work/dev/overview ───────────────────────────────────────────
export const DevOverviewResSchema = z.object({
  quotas: z.array(DevQuotaSchema),
  cards: z.array(DevCardSchema),
  /** Открытые (непринятые) замечания. Группировку по локаль+сервис делает веб. */
  comments: z.array(DevQaCommentSchema),
  /**
   * Недельный бонус ОТДЕЛА — общая цифра, видна всем (задачи в отделе общие).
   * Считается по финально ОТГРУЖЕННОМУ за неделю, не по сданному на QA.
   * Ставка — градация: перевалили за порог — вся неделя идёт по новой ставке.
   */
  bonus: z.object({
    /** Понедельник текущей недели, YYYY-MM-DD. */
    week_start: z.string(),
    shipped: z.number().int().nonnegative(),
    /** Центов за ассет на текущем пороге. 0 — до первого порога не дошли. */
    rate_cents: z.number().int().nonnegative(),
    amount_usd: z.number().nonnegative(),
    /** Следующий порог и его ставка. null — потолок уже взят. */
    next_at: z.number().int().nonnegative().nullable(),
    next_cents: z.number().int().nonnegative().nullable(),
  }),
  /** Цифры отдела. `since` — дата старта учёта ('' = считаем по всему). */
  totals: z.object({
    since: z.string().default(''),
    made: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    on_qa: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
  }),
});
export type DevOverviewRes = z.infer<typeof DevOverviewResSchema>;

// ─── POST /api/work/dev/card — завести заход ──────────────────────────────
// Локаль + типаж + площадка. Квота заводится сама, если её ещё не было:
// новая локаль не должна упираться в сетку.
//
// `target` — ПОТЕНЦИАЛ ПЛОЩАДКИ: сколько реально даст этот заход, а не
// остаток квоты ячейки. До 2026-08-17 кабинет подставлял сюда остаток, и все
// заходы получались одинаковыми: у четырёх гифт-карточных заходов pt_PT стоял
// target 899 при фактических 20 у каждого — отличить площадку на 20 от
// площадки на 800 было нечем ни в кабинете, ни в отчёте.
export const DevCreateCardReqSchema = z.object({
  // Только разрешённые коды проекта (35, vocab/plan): возникший в процессе
  // en_SA/en_CA «не подходит, потому что не в списке» (ADR-0061). Английский
  // контент заводится карточкой каноничной локали страны, английский пишется
  // ассетам в secondary_locale при индексации.
  locale: PlanCodeSchema,
  kind: DevKindSchema,
  source: z.string().trim().max(120).default(''),
  target: z.number().int().nonnegative().max(100_000).default(0),
  note: z.string().trim().max(500).default(''),
});
export type DevCreateCardReq = z.infer<typeof DevCreateCardReqSchema>;

// ─── POST /api/work/dev/card/target — поправить потенциал захода ──────────
// Отдельная ручка, а не поле в «закрыть/сдать»: потенциал уточняется по ходу
// (нашли, что у площадки не 900 событий, а 140), и это не переход этапа.
// Нужна ещё и затем, чтобы разово вычистить заходы, заведённые со старым
// автоподставленным остатком квоты.
export const DevSetCardTargetReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  target: z.number().int().nonnegative().max(100_000),
});
export type DevSetCardTargetReq = z.infer<typeof DevSetCardTargetReqSchema>;

// ─── POST /api/work/dev/card/merchants — добавить мерчантов в карточку ────
// Мембершипы ведутся одной карточкой на страну, а мерчанты набираются внутрь
// по мере работы (решение владельца 2026-08-17). Вписанное количество не
// принимается на веру: сервер сам считает, сколько мерчанту осталось до капа.
export const DevAddMerchantsReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  names: z.array(z.string().trim().min(1).max(120)).min(1).max(100),
});
export type DevAddMerchantsReq = z.infer<typeof DevAddMerchantsReqSchema>;

/** Что стало с каждым именем. `full` — кап мерчанта уже выбран, брать нечего. */
export const DevMerchantRoomSchema = z.object({
  name: z.string(),
  /** Сколько кадров планируем взять: кап − уже отгруженное по нему. */
  planned: z.number().int().nonnegative(),
  /** Сколько его кадров уже уехало заказчику в этой стране. */
  already_shipped: z.number().int().nonnegative(),
  full: z.boolean().default(false),
});
export type DevMerchantRoom = z.infer<typeof DevMerchantRoomSchema>;

export const DevAddMerchantsResSchema = z.object({
  ok: z.boolean(),
  card: DevCardSchema,
  merchants: z.array(DevMerchantRoomSchema),
  /** Ёмкость ячейки на момент ответа — чтобы UI не пересчитывал её сам. */
  cell: z.object({
    plan: z.number().int().nonnegative(),
    shipped: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
    merchants_needed: z.number().int().nonnegative(),
  }),
});
export type DevAddMerchantsRes = z.infer<typeof DevAddMerchantsResSchema>;

// ─── POST /api/work/dev/card/needsDesign — пометить «нужен дизайнер» ──────
// Либо карточку целиком, либо одну позицию внутри неё: у купонного агрегатора
// с пятью видами купонов задизайнить надо бывает две, а не все.
export const DevNeedsDesignReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  /** Имя позиции внутри карточки. Пусто = помечаем карточку целиком. */
  merchant: z.string().trim().max(120).default(''),
  on: z.boolean(),
});
export type DevNeedsDesignReq = z.infer<typeof DevNeedsDesignReqSchema>;

// ─── POST /api/work/dev/card/move — перенести карточку в другую колонку ───
// Доска допускает РУЧНОЙ перенос (перетаскиванием либо кнопкой «проблема с
// манифестом»): автоматика знает дефолт, но не знает причину. Отклонённое на QA
// по умолчанию едет в размножение, а бывает, что чинить надо манифест.
export const DevBoardColumnSchema = z.enum(['new', 'design', 'manifest', 'multiply', 'qa']);
export type DevBoardColumn = z.infer<typeof DevBoardColumnSchema>;

export const DevMoveCardReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  column: DevBoardColumnSchema,
  /** Зачем перенесли. Для «проблемы с манифестом» — обязательна по смыслу. */
  reason: z.string().trim().max(500).default(''),
});
export type DevMoveCardReq = z.infer<typeof DevMoveCardReqSchema>;

// ─── POST /api/work/dev/designer/claim — забрать роль дизайнера ───────────
// Нажал «скачать плагин» — получил роль (решение владельца 2026-08-17). Роль
// выдаётся ДО скачивания и отдельным вызовом: скачивание это GET, а GET,
// меняющий права, не должен существовать — его дёрнет любой префетч.
export const DevClaimDesignerResSchema = z.object({
  ok: z.boolean(),
  /** Уже была роль или выдали сейчас — чтобы UI не врал «роль выдана». */
  granted: z.boolean(),
  token: z.string(),
});
export type DevClaimDesignerRes = z.infer<typeof DevClaimDesignerResSchema>;

// ─── POST /api/work/dev/card/close — закрыть заход ────────────────────────
// 'dropped' обязан нести причину: заход, закрытый молча, через месяц
// неотличим от забытого.
export const DevCloseCardReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  outcome: z.enum(['shipped', 'dropped']),
  drop_reason: z.string().trim().max(500).default(''),
});
export type DevCloseCardReq = z.infer<typeof DevCloseCardReqSchema>;

// ─── POST /api/work/dev/card/submitFixes — сдать доделку ──────────────────
// Отдельное действие, а не «закрыть окно»: пока правки не сданы явно, карточка
// висит в колонке «Правки», и по доске не отличить догруженную доделку от
// забытой (замечание разработчика, 2026-08-07).
export const DevSubmitFixesReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
});
export type DevSubmitFixesReq = z.infer<typeof DevSubmitFixesReqSchema>;


/**
 * Сколько страниц манифеста приходится на одного участника. Жёсткий лимит —
 * ПЯТЬ: один участник идёт в PDF подряд и не больше пяти раз (правило
 * владельца). Считает клиент — он же разбирает файл, — а сервер отбивает пак
 * целиком, если лимит нарушен. Проверка появилась после того, как в проде
 * обнаружились пачки с десятью страницами на человека (2026-08-07).
 */
export const MANIFEST_PAGES_PER_HOLDER = 5;

export const ManifestHolderSchema = z.object({
  name: z.string().trim().min(1).max(160),
  pages: z.number().int().positive(),
});
export type ManifestHolder = z.infer<typeof ManifestHolderSchema>;

// ─── POST /api/work/dev/artifact ──────────────────────────────────────────
// Заливка материала. `reverse` заливать нельзя — его генерирует система.
export const DevUploadArtifactReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  /** Позиция внутри карточки; пусто — материал карточки целиком. */
  item: z.string().trim().max(120).default(''),
  kind: z.enum(['source', 'manifest', 'output', 'master']),
  file_name: z.string().trim().min(1).max(200),
  content_type: z.string().trim().max(120).default('application/octet-stream'),
  /** base64 тела файла (без data:-префикса). */
  file_base64: z.string().min(1).max(40_000_000),
  /**
   * Организаторы/мерчанты, вытащенные из манифеста. Присылает КЛИЕНТ (он же
   * разбирает файл), сервер только схлопывает по имени. Для не-манифестов
   * игнорируется.
   */
  merchants: z.array(z.string().trim().min(1).max(120)).max(500).default([]),
  /** Участники манифеста и число их страниц — для гейта «≤5 на участника». */
  holders: z.array(ManifestHolderSchema).max(2000).default([]),
});
export type DevUploadArtifactReq = z.infer<typeof DevUploadArtifactReqSchema>;

export const DevCardResSchema = z.object({ ok: z.boolean(), card: DevCardSchema });
export type DevCardRes = z.infer<typeof DevCardResSchema>;

// ─── чанковая заливка крупного материала ─────────────────────────────────────
// PDF под печать весит сотни мегабайт: base64-путь (лимит ~40 МБ) для него не
// годится, поэтому begin → N×part (сырые байты, метаданные в заголовках
// X-Upload-Id / X-R2-Key / X-Part-Number) → complete поверх R2 multipart.
export const DevArtifactBeginReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  /** Позиция внутри карточки; пусто — материал карточки целиком. */
  item: z.string().trim().max(120).default(''),
  kind: z.enum(['source', 'manifest', 'output', 'master']),
  file_name: z.string().trim().min(1).max(200),
  content_type: z.string().trim().max(120).default('application/octet-stream'),
  total_size: z.number().int().positive().max(2_000_000_000),
});
export type DevArtifactBeginReq = z.infer<typeof DevArtifactBeginReqSchema>;

export const DevArtifactBeginResSchema = z.object({
  ok: z.boolean(),
  upload_id: z.string(),
  r2_key: z.string(),
  /** Размер части, который клиент обязан соблюдать (кроме последней). */
  part_size: z.number().int().positive(),
  version: z.number().int().positive(),
});
export type DevArtifactBeginRes = z.infer<typeof DevArtifactBeginResSchema>;

export const DevArtifactPartResSchema = z.object({
  ok: z.boolean(),
  part_number: z.number().int().positive(),
  etag: z.string(),
});
export type DevArtifactPartRes = z.infer<typeof DevArtifactPartResSchema>;

export const DevArtifactCompleteReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  /** Позиция внутри карточки; пусто — материал карточки целиком. */
  item: z.string().trim().max(120).default(''),
  kind: z.enum(['source', 'manifest', 'output', 'master']),
  // Реальный uploadId у R2 — ~343 символа (проверено на живом бакете
  // 2026-08-03; max(256) резал ВСЕ complete как bad_request).
  upload_id: z.string().trim().min(1).max(1024),
  r2_key: z.string().trim().min(1).max(512),
  file_name: z.string().trim().min(1).max(200),
  content_type: z.string().trim().max(120).default('application/octet-stream'),
  total_size: z.number().int().positive().max(2_000_000_000),
  version: z.number().int().positive(),
  parts: z.array(z.object({
    part_number: z.number().int().positive(),
    etag: z.string().min(1).max(256),
  })).min(1).max(128),
  /** Как в обычной заливке: организаторов из манифеста разбирает клиент. */
  merchants: z.array(z.string().trim().min(1).max(120)).max(500).default([]),
  holders: z.array(ManifestHolderSchema).max(2000).default([]),
});
export type DevArtifactCompleteReq = z.infer<typeof DevArtifactCompleteReqSchema>;

// ─── GET /api/work/dev/artifact?card_id=&kind= ────────────────────────────
// Отдаёт байты и В ТОМ ЖЕ ЗАПРОСЕ пишет строку журнала — иначе скачивания в
// лог не попадут. Контракт покрывает только query (ответ бинарный).
export const DevDownloadArtifactQuerySchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  /** Позиция внутри карточки; пусто — материал карточки целиком. */
  item: z.string().trim().max(120).default(''),
  kind: DevArtifactKindSchema,
});

// ─── POST /api/work/dev/qa/accept ─────────────────────────────────────────
// «Взять в работу»: замечание принято, карточка уходит на ДОДЕЛКУ (круг 2),
// система собирает обратный манифест отклонённого.
export const DevAcceptQaReqSchema = z.object({
  comment_id: z.string().trim().min(1).max(64),
});
export type DevAcceptQaReq = z.infer<typeof DevAcceptQaReqSchema>;

// ─── POST /api/work/dev/_admin/upsert (X-Admin-Secret) ────────────────────
// Наполнение: квоты по ячейкам, цифры карточек, замечания QA. Все поля кроме
// id — опциональные: шлём только то, что меняем.
export const DevAdminQuotaSchema = z.object({
  id: z.string().trim().min(3).max(80),      // 'sv_SE|ticket_photo'
  locale: z.string().trim().max(12).optional(),
  kind: DevKindSchema.optional(),
  plan: z.number().int().nonnegative().optional(),
  shipped: z.number().int().nonnegative().optional(),
  rejected: z.number().int().nonnegative().optional(),
  note: z.string().trim().max(500).optional(),
});

export const DevAdminCardSchema = z.object({
  id: z.string().trim().min(1).max(64),
  source: z.string().trim().max(120).optional(),
  stage: DevStageSchema.optional(),
  round: z.union([z.literal(1), z.literal(2)]).optional(),
  target: z.number().int().nonnegative().optional(),
  made: z.number().int().nonnegative().optional(),
  on_qa: z.number().int().nonnegative().optional(),
  accepted: z.number().int().nonnegative().optional(),
  rejected: z.number().int().nonnegative().optional(),
  shipped: z.number().int().nonnegative().optional(),
  merchant_plan: z.number().int().nonnegative().optional(),
  merchants: z.array(z.string().trim().min(1).max(120)).max(500).optional(),
  merchants_shipped: z.array(z.string().trim().min(1).max(120)).max(500).optional(),
  outcome: DevOutcomeSchema.optional(),
  drop_reason: z.string().trim().max(500).optional(),
  note: z.string().trim().max(500).optional(),
});

export const DevAdminUpsertReqSchema = z.object({
  quotas: z.array(DevAdminQuotaSchema).max(400).default([]),
  cards: z.array(DevAdminCardSchema).max(200).default([]),
  comments: z.array(z.object({
    card_id: z.string().trim().min(1).max(64),
    text: z.string().trim().min(1).max(2000),
    target: z.enum(['frame', 'manifest', 'pdf']).default('frame'),
    target_label: z.string().trim().max(120).default(''),
    author: z.string().trim().max(120).default('QA'),
  })).max(200).default([]),
});
export type DevAdminUpsertReq = z.infer<typeof DevAdminUpsertReqSchema>;

export const DevAdminUpsertResSchema = z.object({
  ok: z.boolean(),
  quotas_updated: z.number().int().nonnegative(),
  cards_updated: z.number().int().nonnegative(),
  comments_added: z.number().int().nonnegative(),
  /** id, которых нет — молча не глотаем. */
  unknown: z.array(z.string()).default([]),
});
export type DevAdminUpsertRes = z.infer<typeof DevAdminUpsertResSchema>;

// ─── паки → оригиналы (2026-09-11) ────────────────────────────────────────
// Пак = карточка, чьи кадры уже лежат в индексе pothos (`wd-<card>-…`). QA
// проверяет пак рядом с ОРИГИНАЛОМ (слот `source` карточки), а оригинала у
// восьми паков из девяти на QA не было вовсе — экран показывать было нечего.
// Этот список отвечает на один вопрос: каким пакам нужен оригинал.
export const WorkDevPackSchema = z.object({
  card_id: z.string(),
  locale: z.string(),
  kind: DevKindSchema,
  /** Площадка/агрегатор карточки (свободный ввод). */
  source: z.string().default(''),
  merchants: z.array(z.string()).default([]),
  /** Кадры пака в индексе по статусам. */
  on_qa: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  shipped: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  /** Слот «Исходник» карточки: залит ли файл и какой. */
  source_present: z.boolean(),
  source_file: z.string().default(''),
  source_type: z.string().default(''),
  /** Кадров-исходников распаковано (0 при PDF или пока крон не дошёл). */
  source_frames: z.number().int().nonnegative(),
  /** Кадров пака, у которых оригинал уже прописан в индексе (виден в QA). */
  linked: z.number().int().nonnegative(),
  last_indexed_at: z.string().default(''),
});
export type WorkDevPack = z.infer<typeof WorkDevPackSchema>;
export const WorkDevPacksResSchema = z.object({ packs: z.array(WorkDevPackSchema) });
export type WorkDevPacksRes = z.infer<typeof WorkDevPacksResSchema>;
